/**
 * Explanatory copy behind each question's "Why we ask" trigger.
 *
 * WHY THIS IS NOT IN product-config.mjs. That module is imported by both servers and is where
 * lookup keys, prices and the setup fee live. Marketing prose has a different edit cadence, a
 * different reviewer, and no business being in a diff that also touches money. Keeping it
 * separate means a copy tweak can never accidentally land on a price.
 *
 * NO PRICES APPEAR IN THIS FILE. Not in a heading, not in a button label, not "as a reminder".
 * Every amount the sheet shows is resolved from the cart at render time. A literal here would be
 * a second place that states a price, which is the exact failure mode CLAUDE.md forbids: the
 * mockups wrote "Yes, add stock photos - $98" into the button, and that string would have kept
 * promising $98 after the catalog moved.
 *
 * Keys are question `id`s from product-config.mjs, and questionInfo.test.jsx fails if the two
 * files disagree in either direction. `actions[].answerId` must likewise name a real answer on
 * that question, because a button wired to a stale id would silently do nothing.
 *
 * Shape -- three fields, and that is the whole panel:
 *   heading   the SHEET's title; the desktop panel shows no heading
 *   intro     one paragraph, plain language
 *   actions   [{ answerId, label, variant }] buttons that set the answer and close. The sheet
 *             renders these; the desktop panel does not, because the answer rows are on screen
 *
 * REMOVED FIELDS, deliberately, and the list is long because the panel was trimmed hard:
 * `eyebrow` (a category label whose wording varied per panel, directly above a heading that said
 * the same thing), `callout` (an accented "If you skip this" / "Why it matters" block), `note`
 * (a gray "Nothing is charged today" box), `bulletsTitle` + `bullets` (a labelled list of
 * inclusions), and `trigger` (per-question link text, retired when the disclosure became a
 * generic "Details" so it would stop outshouting the question beside it).
 *
 * What that leaves is the shape of the decision the customer is actually making: what this is,
 * one paragraph on why, and the two answers. Everything cut was explanation stacked ABOVE those
 * two buttons, which on a phone is the difference between the choice being on screen and the
 * customer having to scroll a modal sheet to find it.
 *
 * Adding any of them back means adding markup to InfoSheet too -- this file no longer carries
 * them, and questionInfo.test.jsx fails if one reappears.
 */
const questionInfo = {
  'photos-question': {
    heading: 'Why we ask about photos',
    intro:
      'Real photos of your team, your work and your space are the single biggest difference between a site that looks like yours and a site that looks like a template. We include one stock photo per page, and if you do not have 20 usable images we can source more.',
    actions: [
      { answerId: 'photos-no-stock', label: 'Yes, add stock photos', variant: 'primary' },
      { answerId: 'photos-yes', label: 'No thanks, I have photos', variant: 'secondary' },
    ],
  },

  'logo-question': {
    heading: 'What is in a logo package',
    intro:
      'A web-ready logo is a vector file that stays sharp at any size, whether it is on your site, a sign, or an invoice. If what you have is a low-resolution image, or a design that does not hold up small, we will tell you honestly whether it can be used as-is or is worth redrawing.',
    actions: [
      { answerId: 'logo-yes', label: 'Yes, design my logo', variant: 'primary' },
      { answerId: 'logo-no', label: 'No thanks, I have a logo', variant: 'secondary' },
    ],
  },

  'seo-content-question': {
    heading: 'What written content includes',
    intro:
      'A writer interviews you, learns how you actually describe your work, and writes every page around the words your customers search for. You review and approve before anything is published.',
    actions: [
      { answerId: 'seo-content-yes', label: 'Yes, write my content', variant: 'primary' },
      { answerId: 'seo-content-maybe', label: 'Talk to a human first', variant: 'secondary' },
    ],
  },

  'accessibility-question': {
    heading: 'ADA and WCAG accessibility, in plain terms',
    intro:
      'An accessible site works for people using a screen reader, a keyboard instead of a mouse, or a phone at maximum text size. WCAG is the standard that spells out what that means, and the ADA is the US law that increasingly expects it.',
    actions: [
      { answerId: 'accessibility-yes', label: 'Yes, tell me more', variant: 'primary' },
      { answerId: 'accessibility-no', label: 'No thanks', variant: 'secondary' },
    ],
  },

  'ongoing-seo-question': {
    heading: 'How ongoing SEO works',
    intro:
      'Launching a site gets you found for your own name. Ranking for what people actually search, the service plus the town, is steady monthly work: content, local listings, and links.',
    actions: [
      { answerId: 'ongoing-seo-yes', label: 'Yes, reach out to me', variant: 'primary' },
      { answerId: 'ongoing-seo-no', label: 'No thanks', variant: 'secondary' },
    ],
  },

  'paid-ads-question': {
    heading: 'What a paid ads program looks like',
    intro:
      'Ads buy attention while SEO is still building. You set a monthly budget, we build and manage the campaigns, and every dollar is traceable to calls and form fills.',
    actions: [
      { answerId: 'paid-ads-yes', label: 'Yes, I would like to learn more', variant: 'primary' },
      { answerId: 'paid-ads-no', label: 'No thanks', variant: 'secondary' },
    ],
  },
};

export default questionInfo;
